import { useState, useMemo } from "react";
import {
  Heart,
  Calendar,
  FileSignature,
  ChevronDown,
  FileText,
  Pill,
  Syringe,
  FileCheck,
  ImageIcon,
  ClipboardList,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { VitalsItems } from "./VitalsItems";
import { AppointmentItems } from "./AppointmentItems";
import { SignatureItems } from "./SignatureItems";
import useTitanApp from "@/hooks/useTitanApp";
import {
  safeParsePayload,
  getVitalDataFromDetails,
} from "./actionHelperFunctions";
import {
  setOpenPrescriptionDialog,
  setIsPrescriptionUpdate,
  setCurrentOpenVitalsModal,
  setBookingDetails,
  setOpenBookingDialog,
  setReferralLetter,
  setOpenRefDialog,
  setChatBotReferralLetter,
  setVaccinationImageBase64,
  setShowVaccinationPreviewModal,
  getPatientConsentSignature,
  setOpenSignatureDialog,
  setCurrentOpenDeleteVitalsModal,
  getPatientDocumentById,
  setViewOpen,
  setVaccineDetails,
  setOpenVaccineDialog,
  setPrescriptionId,
  setDynamicPrescriptions,
  setGeneratedVaccinationCertificate,
  addPatientDocuments,
  setSelectedPrescriptionCategory,
} from "@/dispatchers/patientDispatchers";
import {
  getPrescriptionByPrescriptionId,
  setPreviewPrescription,
  setConsultationPrescriptionImagePreview,
  setShowPrintPreviewLoading,
} from "@/dispatchers/patientPrescriptionDispatcher";
import { ReferralItems } from "./ReferralItems";
import { DocumentsItems } from "./DocumentsItems";
import { PrescriptionsItems } from "./PrescriptionsItems";
import { setViewingDoc } from "@/dispatchers/documentDispatchers";
import { useDocumentsData } from "@/components/Documents/documentsData";
import { VaccinationsActionItems } from "./VaccinationsActionItems";
import { usePrescriptionsState } from "@/lib/dashboard/prescriptionsData";
import { PrescriptionDraftItem } from "./PrescriptionDraftItem";
import { ImageDocumentActionItems } from "./ImageDocumentActionItems";
import { TreatmentPlanActionItems } from "./TreatmentPlanActionItems";
import { AHTTreatmentPlanCard } from "./AHTTreatmentPlanCard";
import { VaccineDraftItem } from "./VaccineDraftItem";
import { useScheduleData } from "@/components/Scheduler/scheduleData";
import jsPDF from "jspdf";
import PatientConsultationResponse from "@/utils/PatientConsultationResponse";
import { updateReferralSendActionsUrl } from "@/apiUrls/apiUrls";
import { ActionType } from "@/enum/ActionType";
import { NEWS2VitalsCard } from "./NEWS2VitalsCard";
import CustomToast from "@/components/controls/CustomToast";
import { toast } from "react-toastify";

// Place these near the top, after imports

const getNews2RiskLabel = (score: number): string => {
  if (score >= 7) return "High risk";
  if (score >= 5) return "Medium risk";
  return "Low risk";
};

const getNews2RiskBadgeCls = (score: number, isDark: boolean): string => {
  if (score >= 7)
    return isDark
      ? "bg-red-900/40 text-red-300 border-red-700"
      : "bg-red-100 text-red-800 border-red-200";
  if (score >= 5)
    return isDark
      ? "bg-amber-900/40 text-amber-300 border-amber-700"
      : "bg-amber-100 text-amber-800 border-amber-200";
  return isDark
    ? "bg-green-900/40 text-green-300 border-green-700"
    : "bg-green-100 text-green-800 border-green-200";
};

const GROUP_TITLES = {
  vitals: (count: number) =>
    count > 1 ? "Multiple Vitals Recorded" : "Vital Sign Recorded",
  appointments: (count: number) =>
    count > 1 ? "Multiple Appointments" : "Appointment",
  signatures: (count: number) =>
    count > 1 ? "Multiple Signatures" : "Signature",
  documents: (count: number) =>
    count > 1 ? "Documents Uploaded" : "Document Uploaded",
  prescriptions: (count: number) =>
    count > 1 ? "Multiple Prescriptions" : "Prescription",
  vaccinations: (count: number) =>
    count > 1 ? "Multiple Vaccinations" : "Vaccination",
  images: (count: number) => (count > 1 ? "Images Uploaded" : "Image Uploaded"),
};

const GROUP_STYLES = {
  vitals: {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gradient-to-r from-orange-900/30 to-red-900/30 border-orange-600/50"
        : "bg-gradient-to-r from-orange-50 to-red-50 border-orange-400",

    icon: (isDarkMode: boolean) => (
      <Heart
        className={`w-5 h-5 flex-shrink-0 ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
  appointments: {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-600/50"
        : "bg-gradient-to-r from-green-50 to-emerald-50 border-green-400",

    icon: (isDarkMode: boolean) => (
      <Calendar
        className={`w-5 h-5 mt-0.5 flex-shrink-0  ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
  signatures: {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border-indigo-600/50"
        : "bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-400",

    icon: (isDarkMode: boolean) => (
      <FileSignature
        className={`w-5 h-5 mt-0.5 flex-shrink-0  ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
  documents: {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gradient-to-r from-teal-900/30 to-emerald-900/30 border-teal-600/50"
        : "bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-400",

    icon: (isDarkMode: boolean) => (
      <FileText
        className={`w-5 h-5 mt-0.5 flex-shrink-0  ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
  prescriptions: {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-blue-600/50"
        : "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400",
    icon: (isDarkMode: boolean) => (
      <Pill
        className={`w-5 h-5 mt-0.5 flex-shrink-0  ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
  vaccinations: {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-600/50"
        : "bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-400",

    icon: (isDarkMode: boolean) => (
      <Syringe
        className={`w-5 h-5 mt-0.5 flex-shrink-0  ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
  images: {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-600/50"
        : "bg-gradient-to-r from-purple-50 to-pink-50 border-purple-400",
    badge: (isDarkMode: boolean) =>
      "bg-purple-600 hover:bg-purple-700 text-white",
    icon: (isDarkMode: boolean) => (
      <ImageIcon
        className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
  "draft-vaccinations": {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gray-900/10 border-gray-600/30"
        : "bg-gray-100 border-gray-300",

    icon: (isDarkMode: boolean) => (
      <Syringe
        className={`w-5 h-5 mt-0.5 flex-shrink-0  ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
  "treatment-plans": {
    bg: (isDarkMode: boolean) =>
      isDarkMode
        ? "bg-gradient-to-r from-violet-900/30 to-purple-900/30 border-violet-600/50"
        : "bg-gradient-to-r from-violet-50 to-purple-50 border-violet-400",
    icon: (isDarkMode: boolean) => (
      <ClipboardList
        className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      />
    ),
  },
};

const BADGE_TEXTS = {
  vitals: (count: number) => (count === 1 ? "vital" : "vitals"),
  appointments: (count: number) =>
    count === 1 ? "appointment" : "appointments",
  signatures: (count: number) => (count === 1 ? "signature" : "signatures"),
  documents: (count: number) => (count === 1 ? "doc" : "docs"),
  prescriptions: (count: number) =>
    count === 1 ? "prescription" : "prescriptions",
  vaccinations: (count: number) =>
    count === 1 ? "vaccination" : "vaccinations",
  referrals: (count: number) => (count === 1 ? "referral" : "referrals"),
  "draft-prescriptions": (count: number) => (count === 1 ? "draft" : "drafts"),
  "draft-vaccinations": (count: number) =>
    count === 1 ? "vaccine" : "vaccines",
  "treatment-plans": (count: number) => (count === 1 ? "plan" : "plans"),
};

const parseVaccinationData = (item: any): any[] => {
  const details = item.details || {};
  const payload =
    typeof details.payload === "string" ? JSON.parse(details.payload) : details;

  return (
    payload.vaccinationDetails?.vaccinations ||
    payload.calculatorResults ||
    payload.vaccineItems ||
    payload.vaccinations ||
    payload.items ||
    []
  );
  return [];
};

export const ActionGroupCard = ({
  group,
  isDarkMode,
  onDeleteAction,
  draftVaccineCount,
}: any) => {
  const [isOpen, setIsOpen] = useState(true);
  const [sentReferralKeys, setSentReferralKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const style = GROUP_STYLES[group.groupKey];
  // Filter out items with null details for count
  const validItems = useMemo(
    () => group.items.filter((item: any) => item.details !== null),
    [group.items],
  );
  const totalVaccineCount = useMemo(() => {
    if (
      group.groupKey !== "vaccinations" &&
      group.groupKey !== "draft-vaccinations"
    )
      return validItems.length;
    let count = 0;
    validItems.forEach((item: any) => {
      const vaccinations = parseVaccinationData(item);
      count += vaccinations.length;
    });
    return count;
  }, [validItems, group.groupKey]);

  // Use appropriate count based on group type
  const displayCount =
    group.groupKey === "vaccinations"
      ? totalVaccineCount
      : group.groupKey === "draft-vaccinations"
      ? totalVaccineCount
      : validItems.length;

  // Add this after the displayCount computation
  const news2VitalsDetails = useMemo(() => {
    if (group.groupKey !== "vitals") return null;
    for (const item of validItems) {
      try {
        const payload = JSON.parse(item.details?.payload || "{}");
        if (payload.vitalsDetails?.isNews2Session) return payload.vitalsDetails;
      } catch {}
    }
    return null;
  }, [validItems, group.groupKey]);

  const title =
    group.groupKey === "draft-vaccinations"
      ? "Vaccine Drafts"
      : GROUP_TITLES[group.groupKey]?.(displayCount) ?? group.title;

  const badgeText = BADGE_TEXTS[group.groupKey]?.(displayCount) || "items";

  const { appState, patientState } = useTitanApp();
  const { companyId } = appState || {};
  const {
    selectedPatientConsultation,
    consultationId,
    patientDocumentDetails,
    generatedVaccinationCertificate,
    selectedPatientId,
    consultationData,
  } = patientState || {};
  const { fetchDocumentById } = useDocumentsData();
  const { onGenerateVaccineCertificate } = usePrescriptionsState();
  const { servicePharmacists } = useScheduleData();

  // Get the most recent valid item for timestamp display
  const firstlyAddedTime = useMemo(() => {
    if (validItems.length === 0) return null;

    return [...validItems].sort((a: any, b: any) => {
      const dateA = new Date(a.displayTimestamp || a.recordedAt || 0).getTime();
      const dateB = new Date(b.displayTimestamp || b.recordedAt || 0).getTime();
      return dateA - dateB;
    })[0];
  }, [validItems]);

  const firstTimestamp = firstlyAddedTime
    ? new Date(
        firstlyAddedTime.displayTimestamp ||
          firstlyAddedTime.recordedAt ||
          new Date(),
      )
    : null;

  const timestampLabel =
    group.groupKey === "vitals"
      ? "Recorded at"
      : group.groupKey === "appointments" || group.groupKey === "prescriptions"
      ? "Created at"
      : group.groupKey === "signatures"
      ? "Captured at"
      : group.groupKey === "documents"
      ? "Added at"
      : group.groupKey === "vaccinations"
      ? "Administered at"
      : group.groupKey === "draft-vaccinations"
      ? "Click a vaccine to administer it"
      : group.groupKey === "images"
      ? "Uploaded at"
      : "Recorded at";

  const formattedTime = firstTimestamp
    ? firstTimestamp.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  /*Vital Handlers */

  const handleVitalClick = async (action: any) => {
    const parsedDetails = safeParsePayload(action.details);
    const vitalsData = parsedDetails?.vitalsDetails || parsedDetails;
    const vitalInfo =
      action.actionId === 5 ? getVitalDataFromDetails(vitalsData) : null;

    const isActionEnabled = (() => {
      if (action.actionId === 2) return false;
      if (action.actionId === 1) return action.isLatest !== false;
      if (action.actionId === 8) return action.isLatest === true;
      if ([3, 4, 6].includes(action.actionId)) return true;
      if (["prescription-draft", "vaccine-draft"].includes(action.actionId))
        return true;

      return action.isLatest ?? true;
    })();

    if (!isActionEnabled) return;

    setCurrentOpenVitalsModal({
      isEdit: true,
      vital: {
        vitalName: vitalInfo?.vitalType,
        ...vitalInfo?.vitalData,

        height: vitalInfo?.vitalData?.height || vitalsData?.height,
        weight: vitalInfo?.vitalData?.weight || vitalsData?.weight,
        bmi: vitalInfo?.vitalData?.bmi || vitalsData?.bmi,
      },
      vitalType: vitalInfo?.vitalType,
      actionId: action.actionId,
      vitalMessageId: action.vitalMessageId,
    });
  };

  const handleDeleteVital = (item: any) => {
    // Your existing delete vital logic (commented out)
    return;
  };

  /*Appointment Handlers */

  const handleAppointmentClick = (details: any) => {
    let originalAppointmentId = null;
    try {
      const parsedDetails = safeParsePayload(details);
      if (parsedDetails?.id) {
        originalAppointmentId = parsedDetails.id;
      }
    } catch (e) {
      console.warn("Could not parse appointment ID for edit:", e);
    }

    setBookingDetails({
      ...details,
      _originalAppointmentId: originalAppointmentId,
    });
    setOpenBookingDialog(true);
  };

  /*Signature Handlers */

  const handleSignatureClick = async (details: any) => {
    let payload = {
      consultationId,
      signatureId: details.signatureId,
    };
    await getPatientConsentSignature(payload);
    setOpenSignatureDialog({ open: true, viewMode: true, details });
  };

  /*Referral Handlers */

  const handleReferralClick = (details: any) => {
    setReferralLetter(details);
    setOpenRefDialog(true);
    setChatBotReferralLetter(details);
  };

  const getReferralStatusKey = (item: any, details: any) =>
    item?.id ||
    details?.referralReference ||
    `${details?.title || "referral"}-${details?.recordedAt || ""}`;

  const isReferralSent = (item: any, details: any) => {
    const key = getReferralStatusKey(item, details);
    return sentReferralKeys.has(key);
  };

  const handleSendNowClick = async (item: any, details: any) => {
    let pdfFile;
    let base64Image = "";
    const letterToSave = {
      title: details.title,
      subject: details.subject,
      content: details.content,
      recipients: details.recipients.map((r) => ({
        email: r.email,
        label: r.label,
        type: r.type,
      })),
      status: "Sent",
      documentType: details.documentType,
      referralReference: details.referralReference,
    };

    pdfFile = await createPdfBlob(letterToSave.title, letterToSave.content);
    base64Image = convertTextToBase64Image(
      letterToSave.title,
      letterToSave.content,
    );

    const letterPayload = {
      actionName: ActionType[3],
      actionNumber: "3",
      payload: JSON.stringify({
        emailRecipients: letterToSave.recipients,
        includeEmailSignature: false,
        emailCopyToPatient: false,
        letterContent: letterToSave,
        recordedAt: details.recordedAt,
        recipients: letterToSave.recipients,
        status: "Queued",
        documentType: details.documentType,
        referralReference: details.referralReference,
      }),
      isPerformed: true,
      image: base64Image,
      documentType: details.documentType,
      referralReference: details.referralReference,
    };
    try {
      let result = await PatientConsultationResponse(
        { pdfFile, payload: letterPayload },
        () => {},
        updateReferralSendActionsUrl(consultationId),
      );
      if (result?.response === true) {
        const sentKey = getReferralStatusKey(item, details);
        setSentReferralKeys((prev) => new Set(prev).add(sentKey));
      }
    } catch (error) {
      console.error("Error queueing email:", error);
      alert("Failed to queue email. Please try again.");
    } finally {
    }
  };
  const convertTextToBase64Image = (title: string, content: string) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const width = 595;
    const height = 842;
    canvas.width = width;
    canvas.height = height;

    if (!ctx) return "";

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = "#282828";
    ctx.font = "bold 24px Times New Roman";
    ctx.textBaseline = "top";

    const marginLeft = 40;
    let y = 40;

    ctx.fillText(title, marginLeft, y);
    y += 40;

    ctx.font = "normal 18px Times New Roman";
    const lineHeight = 26;
    const maxWidth = width - marginLeft * 2;

    const lines: string[] = [];
    const words = content.split(" ");
    let line = "";

    words.forEach((word) => {
      const testLine = line + word + " ";
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && line) {
        lines.push(line.trim());
        line = word + " ";
      } else {
        line = testLine;
      }
    });

    if (line) lines.push(line.trim());

    lines.forEach((textLine) => {
      if (y > height - 40) {
        return;
      }
      ctx.fillText(textLine, marginLeft, y);
      y += lineHeight;
    });

    return canvas.toDataURL("image/png");
  };

  const createPdfBlob = async (title: string, content: string) => {
    const doc = new jsPDF("p", "mm", "a4");

    const marginLeft = 20;
    const lineHeight = 8;
    const pageWidth = doc.internal.pageSize.getWidth() - marginLeft * 2;

    doc.setFont("Times", "Normal");
    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);

    doc.setFontSize(18);
    doc.setFont("Times", "Bold");
    doc.text(title, marginLeft, 30);

    doc.setFontSize(14);
    doc.setFont("Times", "Normal");

    const lines = doc.splitTextToSize(content, pageWidth);
    let y = 40;

    for (let i = 0; i < lines.length; i++) {
      if (y > 280) {
        doc.addPage();
        y = 20;
      }
      doc.text(lines[i], marginLeft, y);
      y += lineHeight;
    }

    return doc.output("blob");
  };

  /*Document Handlers */

  const handleDocumentClick = async (docDetails: any) => {
    try {
      const documentId = docDetails?.documentId || docDetails?.libraryDocId;
      const isLibraryDocument = docDetails?.libraryDocId ? true : false;

      if (!documentId) {
        console.warn("No document ID found in document data");
        return;
      }

      let result;

      if (isLibraryDocument) {
        result = await fetchDocumentById(
          documentId,
          docDetails.libraryDocFileName,
          1,
        );
      } else {
        result = await getPatientDocumentById(documentId);
      }

      const documentContent = result?.base64Content;
      let fileExtension = result?.fileExtenstion || "";
      const fileName =
        result?.fileName ||
        docDetails?.fileName ||
        docDetails?.title ||
        "Document";

      if (!documentContent) {
        console.warn("No document content received");
        return;
      }

      if (fileExtension && !fileExtension.startsWith(".")) {
        fileExtension = "." + fileExtension;
      }

      const mimeMap: { [key: string]: string } = {
        ".pdf": "application/pdf",
        ".docx":
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".doc": "application/msword",
        ".png": "image/png",
        ".jpg": "image/jpeg",
        ".jpeg": "image/jpeg",
        ".gif": "image/gif",
        ".webp": "image/webp",
        ".txt": "text/plain",
        ".xlsx":
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ".pptx":
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      };

      const mimeType =
        mimeMap[fileExtension.toLowerCase()] || "application/octet-stream";

      // Convert base64 to blob
      let blobUrl: string;

      if (documentContent.startsWith("data:")) {
        // Already has data URI prefix
        blobUrl = documentContent;
      } else {
        // Need to convert raw base64 to blob with data URI
        const byteCharacters = atob(documentContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        blobUrl = URL.createObjectURL(blob);
      }

      setViewingDoc({
        title: fileName,
        url: blobUrl,
        mimeType: mimeType,
      });
      setViewOpen(true);
    } catch (error) {
      console.error("Error loading document:", error);
    }
  };

  /*Prescription Handlers */

  const handlePrescriptionClick = async (prescriptionData) => {
    setIsPrescriptionUpdate(true);
    if (prescriptionData) {
      setPreviewPrescription(true);
      setShowPrintPreviewLoading(true);
      const result = await getPrescriptionByPrescriptionId(
        prescriptionData.prescriptionDetails.prescriptionId,
      );
      setConsultationPrescriptionImagePreview(result?.base64Image || "");
      setShowPrintPreviewLoading(false);
    }
  };

  const handlePrescriptionDraftClick = (details) => {
    const parsedDetails = safeParsePayload(details.details);

    setOpenPrescriptionDialog(true);
    setPrescriptionId(null);
    setIsPrescriptionUpdate(false);
    setSelectedPrescriptionCategory("private");
    if (parsedDetails?.prescriptionItems) {
      const mappedItems = parsedDetails.prescriptionItems.map((item) => {
        let qty = item.quantity;
        if (typeof qty === "string") {
          const match = qty.match(/(\d+)/);
          if (match) qty = match[1];
        }
        const hasId = !item.prescribedItemId?.startsWith("k9x_");
        return {
          ...item,
          value: hasId ? item.value : "",
          prescribedItemId: hasId ? item.prescribedItemId : "",
          quantity: qty,
        };
      });
      setDynamicPrescriptions(mappedItems);
    }
  };

  /*Vaccination Handlers */

  const handleVaccinationClick = (details) => {
    // Regular vaccination click handles certificate preview or details
    setVaccinationImageBase64(details.image);
    // setShowVaccinationPreviewModal(true);
    return;
  };

  const handleVaccinationDraftClick = (action, vaccine) => {
    // Collect ALL vaccines from ALL draft items in the group
    const allVaccines: any[] = [];
    validItems.forEach((draftAction: any) => {
      try {
        const payload =
          typeof draftAction.details?.payload === "string"
            ? JSON.parse(draftAction.details.payload)
            : draftAction.details || {};
        const vaccines =
          payload.calculatorResults ||
          payload.vaccineItems ||
          payload.vaccinations ||
          payload.items ||
          [];
        vaccines.forEach((v: any) => {
          allVaccines.push({
            vaccineName: v.medication || v.vaccineName || "",
            dose: v.dosage || v.dose || "",
            schedule: v.duration || v.schedule || "",
            route: v.route || "",
            notes: v.notes || v.note || "",
          });
        });
      } catch (e) {
        console.warn("Failed to parse draft vaccines", e);
      }
    });

    // Alphabetically sort (mirrors VaccineDraftItem sort order)
    allVaccines.sort((a, b) =>
      (a.vaccineName || "").localeCompare(b.vaccineName || ""),
    );

    // Find the index of the clicked vaccine
    const clickedName = vaccine.medication || vaccine.vaccineName || "";
    const expandedIndex = allVaccines.findIndex(
      (v) => v.vaccineName === clickedName,
    );

    const vaccineData = {
      isDraft: true,
      draftActionId: action.id,
      allVaccines,
      expandedIndex: expandedIndex >= 0 ? expandedIndex : 0,
    };

    setVaccineDetails(vaccineData);
    setOpenVaccineDialog(true);
  };

  const handleGenerateCertificate = async () => {
    const processingToast = toast.loading(
      <CustomToast
        title="Processing Vaccination Certificate"
        description="Please wait while we generate your vaccination certificate..."
      />,
      {
        className: "services-toast",
        position: toast.POSITION.BOTTOM_RIGHT,
        theme: isDarkMode ? "dark" : "light",
      },
    );
    if (group.groupKey !== "vaccinations" || !validItems.length) return;

    const vaccineEntries: any[] = [];

    validItems.forEach((item: any) => {
      const vaccinations = parseVaccinationData(item);
      vaccinations.forEach((vaccine: any) => {
        // Create a unique ID for each vaccine entry
        const entryId = `${item.id}-${vaccine.prescriptionItemId ||
          Date.now()}-${Math.random()}`;

        vaccineEntries.push({
          id: entryId,
          medicationName:
            vaccine.vaccineName || vaccine.brandName || "Unknown Vaccine",
          manufacturer: vaccine.brandName || vaccine.manufacturer || "",
          dose: vaccine.dose || vaccine.volume || "Standard dose",
          site: vaccine.site || "",
          batchNumber: vaccine.batchNumber || "",
          expiryDate: vaccine.expiryDate || "",
          date:
            item.displayTimestamp ||
            item.recordedAt ||
            new Date().toISOString(),
          // Include any other fields that might be needed
          route: vaccine.route || "",
          nextDueDate: vaccine.nextDueDate || "",
          note: vaccine.note || "",
        });
      });
    });

    if (vaccineEntries.length === 0) {
      console.warn("No vaccine entries found to generate certificate");
      return;
    }

    const base64Image = await onGenerateVaccineCertificate?.(vaccineEntries);

    const title = "Vaccination Certificate";

    setGeneratedVaccinationCertificate({
      title,
      vaccines: vaccineEntries,
      generatedAt: new Date().toISOString(),
      vaccineCount: vaccineEntries.length,
    });

    if (base64Image) {
      const cleanedBase64Image = base64Image.replace(
        /^data:image\/png;base64,/,
        "",
      );
      const sizeInBytes = Math.round((cleanedBase64Image.length * 3) / 4);
      const sizeInKB = Math.round(sizeInBytes / 1024);
      const payload = {
        patientId: selectedPatientId || selectedPatientConsultation?.patientId,
        title: title,
        description: "Generated Vaccination Certificate",
        size: `${sizeInKB} KB`,
        status: 1,
        fileContent: cleanedBase64Image,
        contentType: "image/jpeg",
        type: 7,
        consultationId: consultationId,
      };
      toast.update(processingToast, {
        render: (
          <CustomToast
            title="Vaccination Certificate Generated"
            description="Your vaccination certificate has been generated successfully."
          />
        ),
        type: "success",
        isLoading: false,
        autoClose: 3000,
        className: "services-toast",
      });
      try {
        if (!generatedVaccinationCertificate) {
          await addPatientDocuments(payload);
        }
      } catch (error) {
        console.error("Failed to store generated certificate:", error);
      }
    }
  };
  /*Image Document Handlers */

  const handleImageDocClick = async (details: any) => {};

  if (displayCount === 0) {
    return null;
  }

  return (
    <div>
      {group.groupKey !== "referrals" &&
      group.groupKey !== "draft-prescriptions" ? (
        <div
          className={`rounded-lg border ${isDarkMode ? "border-gray-700" : ""}`}
        >
          <div
            className={`p-3 ${
              group.groupKey !== "images" ? "border-b" : ""
            } cursor-pointer select-none ${
              isDarkMode ? "border-gray-700" : ""
            }`}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start flex-1 gap-2">
                {style?.icon?.(isDarkMode)}

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p
                      className={`text-sm font-semibold ${
                        isDarkMode ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      {news2VitalsDetails
                        ? `NEWS2 — ${news2VitalsDetails.news2Vitals?.length ??
                            0} observation${
                            (news2VitalsDetails.news2Vitals?.length ?? 0) !== 1
                              ? "s"
                              : ""
                          }`
                        : title}
                    </p>

                    {news2VitalsDetails ? (
                      // ── NEWS2 case: score badge with risk colour ───────────────────────────
                      <Badge
                        className={`text-xs border font-medium ${getNews2RiskBadgeCls(
                          news2VitalsDetails.news2Score ?? 0,
                          isDarkMode,
                        )}`}
                      >
                        Score {news2VitalsDetails.news2Score ?? 0} ·{" "}
                        {getNews2RiskLabel(news2VitalsDetails.news2Score ?? 0)}
                      </Badge>
                    ) : (
                      <>
                        <Badge
                          className={`text-xs text-gray-700 bg-gray-200 hover:bg-gray-300 ${
                            group.groupKey === "draft-vaccinations"
                              ? "rounded-full"
                              : ""
                          }`}
                        >
                          {displayCount} {badgeText}
                        </Badge>
                        {group.groupKey === "draft-vaccinations" && (
                          <Badge className="ml-1 text-xs font-medium text-gray-700 bg-gray-100 border-none rounded-full hover:bg-gray-100">
                            Draft
                          </Badge>
                        )}
                      </>
                    )}
                  </div>

                  {formattedTime && group.groupKey !== "draft-vaccinations" && (
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {timestampLabel} {formattedTime}
                    </p>
                  )}

                  {group.groupKey === "draft-vaccinations" && (
                    <p
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {timestampLabel}
                    </p>
                  )}
                </div>
              </div>

              {/* Chevron */}
              <ChevronDown
                className={`w-4 h-4 mt-1 transition-transform duration-200 ${
                  isOpen ? "rotate-180" : ""
                } ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              />
            </div>

            {group.groupKey === "vaccinations" && (
              <div className="mt-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGenerateCertificate();
                  }}
                  className="flex items-center justify-center w-full gap-2 px-3 py-2 text-sm font-medium text-white transition-all bg-black rounded-md hover:bg-gray-800"
                >
                  <FileCheck className="w-4 h-4" />
                  {generatedVaccinationCertificate
                    ? "Re-generate Vaccination Certificate"
                    : "Generate Vaccination Certificate"}
                </button>
              </div>
            )}
          </div>

          {/* ITEMS */}
          <div
            className={`transition-all duration-300 ${
              isOpen
                ? group.groupKey === "treatment-plans"
                  ? "max-h-[70vh] overflow-y-auto overflow-x-hidden opacity-100"
                  : "max-h-[1000px] overflow-hidden opacity-100"
                : "max-h-0 overflow-hidden opacity-0"
            }`}
          >
            {/* OLD CARD  */}
            {/* {group.groupKey === "vitals" && (
              <VitalsItems
                items={validItems}
                isDarkMode={isDarkMode}
                handleVitalClick={handleVitalClick}
                onDeleteVital={handleDeleteVital}
              />
            )} */}
            {group.groupKey === "vitals" &&
              (() => {
                const regularItems: any[] = [];
                const news2Items: Array<{ item: any; vitalsDetails: any }> = [];

                validItems.forEach((item: any) => {
                  try {
                    const payload = JSON.parse(item.details?.payload || "{}");
                    if (payload.vitalsDetails?.isNews2Session) {
                      news2Items.push({
                        item,
                        vitalsDetails: payload.vitalsDetails,
                      });
                    } else {
                      regularItems.push(item);
                    }
                  } catch {
                    regularItems.push(item);
                  }
                });

                return (
                  <>
                    {regularItems.length > 0 && (
                      <VitalsItems
                        items={regularItems}
                        isDarkMode={isDarkMode}
                        handleVitalClick={handleVitalClick}
                        onDeleteVital={handleDeleteVital}
                      />
                    )}
                    {news2Items.map(({ vitalsDetails }, idx) => (
                      <NEWS2VitalsCard
                        key={idx}
                        isDarkMode={isDarkMode}
                        news2Score={vitalsDetails.news2Score ?? 0}
                        riskLabel={vitalsDetails.riskLabel ?? ""}
                        vitals={vitalsDetails.news2Vitals ?? []}
                      />
                    ))}
                  </>
                );
              })()}

            {group.groupKey === "appointments" && (
              <AppointmentItems
                items={validItems}
                isDarkMode={isDarkMode}
                handleAppointmentClick={handleAppointmentClick}
                servicePharmacists={servicePharmacists}
              />
            )}

            {group.groupKey === "signatures" && (
              <SignatureItems
                items={validItems}
                isDarkMode={isDarkMode}
                handleSignatureClick={handleSignatureClick}
              />
            )}

            {group.groupKey === "documents" && (
              <DocumentsItems
                items={validItems}
                isDarkMode={isDarkMode}
                handleDocumentClick={handleDocumentClick}
              />
            )}
            {group.groupKey === "prescriptions" && (
              <PrescriptionsItems
                items={validItems}
                isDarkMode={isDarkMode}
                handlePrescriptionClick={handlePrescriptionClick}
              />
            )}
            {group.groupKey === "vaccinations" && (
              <VaccinationsActionItems
                items={validItems}
                isDarkMode={isDarkMode}
                handleVaccinationClick={handleVaccinationClick}
                handleGenerateCertificate={handleGenerateCertificate}
                generatedVaccinationCertificate={
                  generatedVaccinationCertificate
                }
              />
            )}
            {group.groupKey === "draft-vaccinations" && (
              <VaccineDraftItem
                items={validItems}
                isDarkMode={isDarkMode}
                handleVaccinationClick={handleVaccinationDraftClick}
                onDelete={onDeleteAction}
                consultationId={consultationId}
                draftVaccineCount={draftVaccineCount}
              />
            )}
            {group.groupKey === "images" && (
              <ImageDocumentActionItems
                items={validItems}
                isDarkMode={isDarkMode}
                handleImageDocClick={handleImageDocClick}
                consultationId={consultationId}
              />
            )}
            {group.groupKey === "treatment-plans" &&
              (() => {
                const ahtItems: any[] = [];
                const standardItems: any[] = [];
                validItems.forEach((item: any) => {
                  try {
                    const payload =
                      typeof item.details?.payload === "string"
                        ? JSON.parse(item.details.payload)
                        : item.details || {};
                    if (payload.resolvedDrugId) {
                      ahtItems.push({ item, payload });
                    } else {
                      standardItems.push(item);
                    }
                  } catch {
                    standardItems.push(item);
                  }
                });
                return (
                  <>
                    {ahtItems.map(({ payload }, idx) => (
                      <div key={`aht-plan-${idx}`} className="p-3">
                        <AHTTreatmentPlanCard
                          plan={payload}
                          isDarkMode={isDarkMode}
                        />
                      </div>
                    ))}
                    {standardItems.length > 0 && (
                      <TreatmentPlanActionItems
                        items={standardItems}
                        isDarkMode={isDarkMode}
                        onDeletePlan={onDeleteAction}
                        consultationId={consultationId}
                        consultationData={consultationData}
                      />
                    )}
                  </>
                );
              })()}
          </div>
        </div>
      ) : (
        /* For referrals, render individual cards directly */
        <div className="space-y-3">
          {group.items.map((item: any, index: number) =>
            group.groupKey === "referrals" ? (
              <ReferralItems
                key={item.id || index}
                items={[item]}
                isDarkMode={isDarkMode}
                handleReferralClick={handleReferralClick}
                handleSendNowClick={handleSendNowClick}
                isReferralSent={isReferralSent}
              />
            ) : (
              <PrescriptionDraftItem
                key={item.id || index}
                items={[item]}
                isDarkMode={isDarkMode}
                handlePrescriptionDraftClick={handlePrescriptionDraftClick}
                onDelete={onDeleteAction}
                consultationId={consultationId}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
};
